import React from 'react'
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  CircularProgress,
  Box,
  Slider
} from '@mui/material'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { SupplierOrigin } from '../../../interfaces/Supplier/SupplierOrigin/SupplierOrigin'
import { SuppliersDDL } from '../../../components/Supplier/SuppliersDDL'
import { useSuppliers } from '../../../hooks/useSuppliers'

interface SupplierOriginFormProps {
  onSubmit: (data: SupplierOrigin) => void
  initialData?: SupplierOrigin
  isEditing?: boolean
}

const validationSchema = Yup.object().shape({
  supplierId: Yup.number()
    .min(1, 'Debe seleccionar un proveedor')
    .required('Proveedor es requerido'),
  supplierOriginName: Yup.string()
    .trim()
    .min(1, 'Nombre del origen es requerido')
    .max(100, 'Nombre del origen no puede tener más de 100 caracteres')
    .required('Nombre del origen es requerido'),
  productControlPercentage: Yup.number()
    .min(0, 'El porcentaje debe ser mayor o igual a 0')
    .max(100, 'El porcentaje debe ser menor o igual a 100')
    .required('Porcentaje de control es requerido'),
  comments: Yup.string().max(
    500,
    'Los comentarios no pueden tener más de 500 caracteres'
  )
})

const initialValues: SupplierOrigin = {
  id: 0,
  supplierOriginId: 0,
  supplierId: 0,
  supplierName: '',
  supplierOriginName: '',
  productControlPercentage: 0,
  comments: '',
  draft: false,
  active: true
}

export const SupplierOriginForm: React.FC<SupplierOriginFormProps> = ({
  onSubmit,
  initialData,
  isEditing = false
}) => {
  const {
    suppliersState: { suppliers }
  } = useSuppliers()

  const handleSupplierChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any
  ) => {
    const supplierId = parseInt(event.target.value)
    const selectedSupplier = suppliers?.find((s) => s.supplierId === supplierId)

    setFieldValue('supplierId', supplierId)
    setFieldValue('supplierName', selectedSupplier?.supplierName || '')
  }

  const handleSubmitWithValidation = (values: SupplierOrigin) => {
    // Limpiar espacios en blanco del nombre del origen
    const cleanedValues = {
      ...values,
      supplierOriginName: values.supplierOriginName.trim(),
      comments: values.comments.trim()
    }
    onSubmit(cleanedValues)
  }

  return (
    <Formik
      initialValues={initialData || initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitWithValidation}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        isValid,
        dirty,
        isSubmitting
      }) => (
        <Form>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <SuppliersDDL
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleSupplierChange(event, setFieldValue)
                }
                value={values.supplierId || 0}
                type='outlined'
                fullWidth={true}
                required={true}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                name='supplierOriginName'
                label='Origen'
                fullWidth
                variant='outlined'
                value={values.supplierOriginName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.supplierOriginName &&
                  Boolean(errors.supplierOriginName)
                }
                helperText={
                  touched.supplierOriginName && errors.supplierOriginName
                }
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                <Typography id='productControlPercentage-slider' gutterBottom>
                  Porcentaje de Control: {values.productControlPercentage}%
                </Typography>
                <Slider
                  value={values.productControlPercentage}
                  onChange={(_, newValue) => {
                    const value = Array.isArray(newValue)
                      ? newValue[0]
                      : newValue
                    setFieldValue('productControlPercentage', value)
                  }}
                  aria-labelledby='productControlPercentage-slider'
                  step={1}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 25, label: '25%' },
                    { value: 50, label: '50%' },
                    { value: 75, label: '75%' },
                    { value: 100, label: '100%' }
                  ]}
                  min={0}
                  max={100}
                  valueLabelDisplay='auto'
                  disabled={isSubmitting}
                />
                {touched.productControlPercentage &&
                  errors.productControlPercentage && (
                    <Typography
                      variant='caption'
                      color='error'
                      style={{
                        fontSize: '0.75rem',
                        marginTop: 4,
                        display: 'block'
                      }}
                    >
                      {errors.productControlPercentage}
                    </Typography>
                  )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                name='comments'
                label='Comentarios'
                fullWidth
                variant='outlined'
                multiline
                rows={3}
                value={values.comments}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.comments && Boolean(errors.comments)}
                helperText={touched.comments && errors.comments}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Field
                    as={Checkbox}
                    name='draft'
                    checked={values.draft}
                    onChange={handleChange}
                    color='primary'
                    disabled={isSubmitting}
                  />
                }
                label='Requiere Draft'
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Field
                    as={Checkbox}
                    name='active'
                    checked={values.active}
                    onChange={handleChange}
                    color='primary'
                    disabled={isSubmitting}
                  />
                }
                label='Activo'
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                fullWidth
                size='large'
                disabled={!isValid || !dirty || isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : undefined
                }
              >
                {isSubmitting
                  ? 'Procesando...'
                  : `${isEditing ? 'Actualizar' : 'Crear'} Origen`}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
